// Library Methods
import { useCallback, useContext, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import ReportPatientHeader from "../ReportSections/ReportPatientHeader/ReportPatientHeader";
import ProgressionReportColumns from "../ProgressionReportColumns/ProgressionReportColumns";
import ReportCommentList from "../ReportSections/ReportCommentList/ReportCommentList";
import ProgressionPatternCharts from "../ProgressionPatternCharts/ProgressionPatternCharts";
import ProgressionExternalExamRow from "../ProgressionExternalExamRow/ProgressionExternalExamRow";
import ProgressionGPACharts from "./ProgressionGPACharts/ProgressionGPACharts";
import ProgressionTrendlines from "../ProgressionTrendlines/ProgressionTrendlines";
import UpdateBaselineModal from "../ReportSections/ReportPatientHeader/UpdateBaselineModal/UpdateBaselineModal";
import { BaselineActionType } from "../ProgressionBaselineButton/ProgressionBaselineButton";

// Hooks
import useGPAGeneration from "src/hooks/progression-analyses/useGPAGeneration";
import { useProgressionAnalysisTrendlines } from "src/services/exams/hooks/progression-analyses/useProgressionAnalysisTrendlines";

// Utils
import { sortByOldestDate } from "src/utils/date/dateHelper";
import { formatProgressionAnalysisData } from "src/utils/exams/examReportsHelper";

// Types
import {
  EnrichedExternalExamType,
  EnrichedRetinaLogikSectionType,
  GPADataType,
  GPAExclusionReason,
  GPAVisitType,
  ProgressionAnalysisTrendlinesType,
} from "src/services/exams/types";

// Contexts
import { ReportContext } from "src/contexts/reports/ReportContext";
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

type ReportSectionType = {
  patientId: string;
  analysisId: string;
  enrichedExams: Array<EnrichedRetinaLogikSectionType>;
  enrichedExternalExams: Array<EnrichedExternalExamType>;
};

const ProgressionReportSection = ({
  patientId,
  analysisId,
  enrichedExams,
  enrichedExternalExams,
}: ReportSectionType) => {
  const { patient, progressionAnalysis } = useContext(ReportContext);
  const { commentData } = useContext(ReportSectionContext);
  const { clinicInfo } = useClinicContext();

  const theme = useTheme();
  const widerThanMdScreen = useMediaQuery(theme.breakpoints.up("md"));

  const [openBaselineModal, setOpenBaselineModal] = useState(false);

  const { analysisData, isLoading, isError, isSkipped } = useGPAGeneration({
    analysisId,
    patientId,
    clinicId: clinicInfo?.id,
  });

  const eyeAnalysis = useMemo(() => {
    const eyeKey = progressionAnalysis?.eye as keyof GPADataType["analyses"];
    if (!eyeKey) return null;

    return analysisData?.analyses?.[eyeKey] ?? null;
  }, [progressionAnalysis?.eye, analysisData]);

  const { data: trendlines, isLoading: isLoadingTrendlines } =
    useProgressionAnalysisTrendlines(clinicInfo?.id, { patientId, analysisId });

  // The analysed eye's trendline visits; without any, the old line charts show
  const trendlineVisits = useMemo(() => {
    const eyeKey =
      progressionAnalysis?.eye as keyof ProgressionAnalysisTrendlinesType["analyses"];
    if (!eyeKey) return [];

    return trendlines?.analyses?.[eyeKey]?.visits ?? [];
  }, [progressionAnalysis?.eye, trendlines]);

  const visits = eyeAnalysis?.visits;
  const hasVisits = Array.isArray(visits) && visits.length > 0;

  // Matches each new-format GPA chart to the exam row it belongs to.
  const visitsByExamId = useMemo(() => {
    const map = new Map<string, GPAVisitType>();
    if (!hasVisits) return map;

    visits.forEach((visit) => {
      (visit.examIds ?? []).forEach((examId) => map.set(examId, visit));
    });
    return map;
  }, [visits, hasVisits]);

  const baselineExamIds = useMemo(
    () => new Set(eyeAnalysis?.baseline?.examIds ?? []),
    [eyeAnalysis?.baseline?.examIds]
  );

  // Every exam gets a row, but only follow-ups GPA actually charted have a
  // visit: the baseline pair is the reference itself, and GPA keeps just one
  // test per calendar day, so a same-day repeat is dropped. Say which it is
  // instead of leaving the row's GPA column unexplainably empty. Only when
  // visits[] exists -- legacy single-snapshot blobs have no per-exam mapping,
  // so every row would be mislabelled as excluded.
  const gpaExclusionFor = useCallback(
    (examId: string): GPAExclusionReason | undefined => {
      if (!hasVisits || visitsByExamId.has(examId)) return undefined;
      return baselineExamIds.has(examId) ? "baseline" : "superseded";
    },
    [hasVisits, visitsByExamId, baselineExamIds]
  );

  const progressionChartData = useMemo(() => {
    const analysisExams = [
      ...enrichedExams,
      ...enrichedExternalExams.map((e) => ({
        ...e,
        completionDate: e?.data?.completionDate,
      })),
    ];
    const sortedExams = sortByOldestDate(
      analysisExams,
      "completionDate"
    ) as Array<EnrichedRetinaLogikSectionType | EnrichedExternalExamType>;
    return formatProgressionAnalysisData(
      sortedExams,
      patient?.dateOfBirth,
      clinicInfo?.timezone
    );
  }, [
    enrichedExams,
    enrichedExternalExams,
    patient?.dateOfBirth,
    clinicInfo?.timezone,
  ]);

  const sortedExamRows = useMemo(() => {
    const rows = [
      ...enrichedExams.map((exam) => ({
        key: `retinalogik-${exam.id}`,
        completionDate: exam.completionDate,
        retinaLogikExam: exam,
        externalExam: null,
      })),
      ...enrichedExternalExams.map((exam) => ({
        key: `external-${exam.id}`,
        completionDate: exam?.data?.completionDate,
        retinaLogikExam: null,
        externalExam: exam,
      })),
    ];

    return sortByOldestDate(rows, "completionDate") as typeof rows;
  }, [enrichedExams, enrichedExternalExams]);

  // Completion date of the oldest baseline exam; the rows are sorted oldest
  // first, so it's the first row that's part of the baseline
  const earliestBaselineDate = useMemo(
    () =>
      sortedExamRows.find((row) =>
        baselineExamIds.has(
          row.retinaLogikExam
            ? row.retinaLogikExam.examId
            : row.externalExam!.id
        )
      )?.completionDate,
    [sortedExamRows, baselineExamIds]
  );

  // The analysis's saved baseline pair, which the baseline modal edits. With none chosen yet
  // every row offers to add one; otherwise only the baseline rows offer to change it.
  const savedBaselineExamIds = progressionAnalysis?.baselineExamIds;
  const savedBaselineExternalExamIds =
    progressionAnalysis?.baselineExternalExamIds;
  const hasSavedBaselines =
    (savedBaselineExamIds?.length ?? 0) +
      (savedBaselineExternalExamIds?.length ?? 0) >
    0;

  const baselineActionFor = useCallback(
    (examId: string, isExternal: boolean): BaselineActionType | undefined => {
      if (!hasSavedBaselines) return "add";
      const savedIds = isExternal
        ? savedBaselineExternalExamIds
        : savedBaselineExamIds;
      return savedIds?.includes(examId) ? "update" : undefined;
    },
    [hasSavedBaselines, savedBaselineExamIds, savedBaselineExternalExamIds]
  );

  const openBaselinePicker = useCallback(() => setOpenBaselineModal(true), []);

  return (
    <Grid
      className="retinalogik-section"
      container
      item
      xs={12}
      lg={8}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <ReportPatientHeader />
      <ProgressionReportColumns />
      <ProgressionGPACharts
        isLoading={isLoading}
        isError={isError}
        isSkipped={isSkipped}
        eyeAnalysis={eyeAnalysis}
      />

      <Grid
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {sortedExamRows.map((row) =>
          row.retinaLogikExam ? (
            <ProgressionPatternCharts
              key={row.key}
              examData={row.retinaLogikExam}
              examReport={row.retinaLogikExam.reportData}
              gpaVisit={visitsByExamId.get(row.retinaLogikExam.examId)}
              gpaExclusion={gpaExclusionFor(row.retinaLogikExam.examId)}
              isGpaLoading={isLoading}
              earliestBaselineDate={earliestBaselineDate}
              baselineAction={baselineActionFor(
                row.retinaLogikExam.examId,
                false
              )}
              onBaselineClick={openBaselinePicker}
            />
          ) : (
            <ProgressionExternalExamRow
              key={row.key}
              externalExam={row.externalExam!}
              gpaVisit={visitsByExamId.get(row.externalExam!.id)}
              gpaExclusion={gpaExclusionFor(row.externalExam!.id)}
              earliestBaselineDate={earliestBaselineDate}
              baselineAction={baselineActionFor(row.externalExam!.id, true)}
              onBaselineClick={openBaselinePicker}
            />
          )
        )}
      </Grid>
      <Box sx={{ height: "fit-content" }}>
        <ProgressionTrendlines
          visits={trendlineVisits}
          eye={progressionAnalysis?.eye ?? ""}
          isLoading={isLoadingTrendlines}
          chartData={progressionChartData}
          displayHorizontally={widerThanMdScreen}
        />
      </Box>
      {commentData?.length > 0 && <ReportCommentList />}
      <UpdateBaselineModal
        open={openBaselineModal}
        onClose={() => setOpenBaselineModal(false)}
        title={hasSavedBaselines ? "Update Baseline" : "Add Baseline"}
      />
    </Grid>
  );
};

export default ProgressionReportSection;
